import Alert from "@components/ui/alert";
import Button from "@components/ui/button";
import Input from "@components/ui/input";
import PasswordInput from "@components/ui/password-input";
import { useRouter } from "next/router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ROUTES } from "@utils/routes";
import { useLoginMutation } from "@data/user/use-login.mutation";
import { useTranslation } from "next-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { getTwoFactorAuthCredentials, hasAccess, setAuthCredentials } from "@utils/auth-utils";
import { CUSTOMER } from "@utils/constants";
import { useModalAction } from "@components/ui/modal/modal.context";

type FormValues = {
  login_id: string;
  password: string;
};
const loginFormSchema = yup.object().shape({
  login_id: yup
    .string()
    .email("form:error-email-format")
    .required("form:error-email-required"),
  password: yup.string().required("form:error-password-required"),
});
const defaultValues = {
  login_id: "",
  password: "",
};

const LoginForm = () => {
  const { mutate: login, isLoading: loading } = useLoginMutation();
  const [errorMsg, setErrorMsg] = useState("");
  const { openModal, closeModal } = useModalAction();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,

    formState: { errors },
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(loginFormSchema),
  });
  const router = useRouter();

  function handleLogin({ login_id, password, pin }: FormValues & { pin?: string }) {
    const trust_token = getTwoFactorAuthCredentials();
    login(
      {
        variables: {
          login_id,
          type: "email",
          password,
          pin,
          ...(trust_token ? { trust_token } : {})
        },
      },
      {
        onSuccess: ({ data }) => {
          if (data?.authentication || data?.auth_pin_required) {
            openModal("2FA_AUTHENTICATION_VIEW", {
              authentication: data.authentication,
              auth_pin_required: data.auth_pin_required
            }, {
              confirmCallback: (pin: string) => {
                closeModal();
                if (data.auth_pin_required) {
                  handleLogin({
                    login_id,
                    password,
                    pin,
                  })
                }
              }
            });
            if (data.auth_pin_required) return;
          }
          if (data?.token) {
            if (!hasAccess([CUSTOMER], data?.roles)) {
              setAuthCredentials(data?.token, data?.roles, data?.permissions);
              router.push(ROUTES.DASHBOARD);
              return;
            }
            setErrorMsg("form:error-enough-permission");
          } else {
            setErrorMsg("form:error-credential-wrong");
          }
        },
        onError: () => {},
      }
    );
  }

  function onSubmit(values: FormValues) {
    handleLogin(values);
  }
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Input
          label={t("form:input-label-email")}
          {...register("login_id")}
          type="email"
          variant="outline"
          className="mb-4"
          error={t(errors?.login_id?.message!)}
        />
        <PasswordInput
          label={t("form:input-label-password")}
          forgotPassHelpText={t("form:input-forgot-password-label")}
          {...register("password")}
          error={t(errors?.password?.message!)}
          variant="outline"
          className="mb-4"
          forgotPageLink={ROUTES.FORGOT_PASSWORD}
        />
        <Button className="w-full" loading={loading} disabled={loading}>
          {t("form:button-label-login")}
        </Button>

        {errorMsg ? (
          <Alert
            message={t(errorMsg)}
            variant="error"
            closeable={true}
            className="mt-5"
            onClose={() => setErrorMsg("")}
          />
        ) : null}
      </form>
    </>
  );
};

export default LoginForm;
